import { Component, computed, effect, inject } from '@angular/core';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { OrderListModule } from 'primeng/orderlist';
import { KeyValuePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { DragDropModule } from 'primeng/dragdrop';
import { Bookmark } from '../../../models/bookmark';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { MenuItem, MenuItemCommandEvent, MessageService } from 'primeng/api';
import { ContextMenuModule } from 'primeng/contextmenu';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { BaseListComponent } from '../base-list/base-list.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { BookmarkListComponent } from '../bookmark-list/bookmark-list.component';
import { RoutingService } from '../../../services/routing.service';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { StateFilter } from '../../../stores/crud.state';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TriStateCheckboxModule } from 'primeng/tristatecheckbox';
import { TagFilterDropdownComponent } from '../../atoms/tag-filter-dropdown/tag-filter-dropdown.component';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { PermissionService } from '../../../services/permission.service';
import { checkVisibleMenuItems } from '../../../utility/array.utility';
import { ButtonModule } from 'primeng/button';
import { GlobalsStore } from '../../../stores/globals.state';
import { getState } from '@ngrx/signals';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { BookmarkValidationPanelComponent } from '../bookmark-validation-panel/bookmark-validation-panel.component';
import { ViewChild } from '@angular/core';

@Component({
  selector: 'app-bookmark-administration',
  standalone: true,
  imports: [
    OrderListModule,
    NgIf,
    DragDropModule,
    NgForOf,
    ContextMenuModule,
    HeaderComponent,
    MaskComponent,
    SidebarModule,
    BookmarkListComponent,
    FloatLabelModule,
    InputTextModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    TriStateCheckboxModule,
    TagFilterDropdownComponent,
    DividerModule,
    MenuModule,
    ButtonModule,
    ButtonGroupModule,
    BookmarkValidationPanelComponent,
  ],
  templateUrl: './bookmark-administration.component.html',
  styleUrl: './bookmark-administration.component.scss',
})
export class BookmarkAdministrationComponent extends BaseListComponent<BookmarkCategory> {
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-12',
    icon: 'fas fa-note-sticky',
    headerText: 'Ansichten',
  };

  readonly store = inject(BookmarkCategoryStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly globalStore = inject(GlobalsStore);

  invalidBookmarks = computed(() =>
    String(this.globalStore.bookmarkValidation()?.invalidBookmarks || '')
  );

  @ViewChild(BookmarkValidationPanelComponent)
  validationPanel!: BookmarkValidationPanelComponent;

  override sidebarId = 'bookmark-administration';

  unsortedBookmarks2: Bookmark[] = [];
  categories: BookmarkCategory[] = [];
  atlasNavigation: string[] = [];
  filterQuery: StateFilter = {
    query: '',
    category: [],
    locked: null,
    published: null,
    tags: [],
    products: [],
    ids: undefined,
  };

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private permissionService: PermissionService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );

    effect(() => {
      const globalState = getState(this.globalStore);
      this.atlasNavigation =
        globalState.activeCommune?.atlasConfig?.navigation || [];
    });

    setTimeout(() => {
      this.sidebarService.setConfiguration(this.sidebarConfig);
      this.unsortedBookmarks2 = this.bookmarkStore.unsorted();
    }, 10);
  }

  /**
   * Updates the filter of the bookmark store with the current filter query.
   * Applies the update operation with a short delay using a timeout to ensure appropriate filtering.
   *
   * @return {void} Does not return a value.
   */
  updateFilter(): void {
    setTimeout(() => {
      this.bookmarkStore.updateFilter(this.filterQuery);
    }, 10);
  }

  /**
   * Handles the drop event for drag-and-drop functionality between containers.
   * Updates the positions, categories, and privacy settings of bookmarks based on the new container.
   *
   * @param {CdkDragDrop<Bookmark[]>} event - The drag-and-drop event that contains information about the previous and current containers, indices, and data.
   * @return {void} This method does not return any value.
   */
  drop(event: CdkDragDrop<Bookmark[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      if (this.canDrop(event)) {
        transferArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex
        );
      }
    }

    const newId: string = event.container.id;
    event.container.data.map((bookmark: Bookmark, index: number) => {
      bookmark.sorting = index;
      bookmark.category =
        newId === 'private' || newId === 'unsorted' ? '' : newId;

      bookmark.private = bookmark.private || newId === 'private';
    });

    this.bookmarkStore.updateMultiple({
      items: event.container.data,
      message: {
        severity: 'success',
        summary: 'Verschieben erfolgreich!',
      },
    });
  }

  /**
   * Determines whether the item can be dropped into the target container based on access controls.
   *
   * @param {CdkDragDrop<Bookmark[]>} event - The drag-and-drop event containing information about the item and containers involved in the operation.
   * @return {boolean} True if the item can be dropped into the target container, false otherwise.
   */
  canDrop(event: CdkDragDrop<Bookmark[]>): boolean {
    const isPrivateContainer =
      event.container.id === 'private' ||
      this.store.isPrivate(event.container.id);
    const createdByUser = event.item.data.user;
    if (
      isPrivateContainer &&
      this.activeUserStore.user()?._id !== createdByUser
    ) {
      this.showDenyToast();
      return false;
    }
    return true;
  }

  /**
   * A custom tracking function for items, based on their unique key property.
   *
   * @param {number} index - The index of the current item in the collection.
   * @param {any} item - The item object containing a key property used for tracking.
   * @return {string} - The key property of the provided item.
   */
  trackByKey(index: number, item: any): string {
    return item.key;
  }

  /**
   * Initializes groups of bookmarks by ensuring that each category has an entry in the result,
   * even if there are no bookmarks for that category.
   *
   * @param {Object<string, Bookmark[]>} groupedBookmarks - An object where the keys represent category ids
   * and the values are arrays of bookmarks belonging to those categories.
   * @param {BookmarkCategory[]} categories - An array of bookmark category objects, each containing an id and other metadata.
   * @return {Object<string, Bookmark[]>} An object where each category id from the provided categories is a key,
   * and its value is an array of corresponding bookmarks. If no bookmarks exist for a given category,
   * the value will be an empty array.
   */
  initializedGroups(
    groupedBookmarks: { [key: string]: Bookmark[] },
    categories: BookmarkCategory[]
  ): { [key: string]: Bookmark[] } {
    const result: { [key: string]: Bookmark[] } = {};
    categories.forEach((cat: BookmarkCategory) => {
      result[cat._id] = groupedBookmarks[cat._id] || [];
    });

    return result;
  }

  /**
   * Handles the event when the context menu for a specific bookmark category is opened.
   *
   * @param {any} event - The event object associated with opening the context menu.
   * @param {BookmarkCategory} bookmarkCategory - The specific bookmark category for which the context menu is opened.
   * @param {BookmarkCategory[]} categories - The list of all bookmark categories available.
   * @return {void} No return value. This method performs actions such as updating the context menu state.
   */
  onCategoryContextMenuOpen(
    event: any,
    bookmarkCategory: BookmarkCategory,
    categories: BookmarkCategory[]
  ): void {
    this.updateContextMenuBookmarkCategories(bookmarkCategory, categories);
  }

  contextMenuBookmarkCategories: MenuItem[] = [];

  /**
   * Updates the context menu for bookmark categories with appropriate actions and visibility based on permissions and settings.
   *
   * @param {BookmarkCategory} bookmarkCategory - The bookmark category for which the context menu is being updated.
   * @param {BookmarkCategory[]} categories - A list of all bookmark categories used to determine the order of actions.
   * @return {void} This method does not return any value; it updates the context menu for bookmark categories.
   */
  updateContextMenuBookmarkCategories(
    bookmarkCategory: BookmarkCategory,
    categories: BookmarkCategory[]
  ): void {
    const items: MenuItem[] = [
      {
        visible: this.permissionService.checkBookmarkRights(
          bookmarkCategory._id,
          'edit'
        ),
        label: 'bearbeiten',
        icon: 'fas fa-pen',
        command: event => this.editBookmarkCategory(bookmarkCategory),
      },
      {
        visible: this.permissionService.checkBookmarkRights(
          bookmarkCategory._id,
          'edit'
        ),
        label: 'löschen',
        icon: 'fas fa-trash',
        command: event => this.deleteBookmarkCategory(event, bookmarkCategory),
      },
      {
        visible: !bookmarkCategory.private,
        separator: true,
      },
      {
        visible:
          this.permissionService.checkBookmarkRights(
            bookmarkCategory._id,
            'edit'
          ) && !bookmarkCategory.private,
        label: 'vor',
        icon: 'fas fa-angle-left',
        disabled: categories.indexOf(bookmarkCategory) === 0,
        command: event => this.moveToIndex(bookmarkCategory, categories, 'up'),
      },
      {
        visible:
          this.permissionService.checkBookmarkRights(
            bookmarkCategory._id,
            'edit'
          ) && !bookmarkCategory.private,
        label: 'zurück',
        disabled:
          categories.indexOf(bookmarkCategory) === categories.length - 1,
        icon: 'fas fa-angle-right',
        command: event =>
          this.moveToIndex(bookmarkCategory, categories, 'down'),
      },
      {
        visible:
          this.permissionService.checkBookmarkRights(
            bookmarkCategory._id,
            'edit'
          ) && !bookmarkCategory.private,
        label: 'Anfang',
        icon: 'fas fa-angles-left',
        disabled: categories.indexOf(bookmarkCategory) === 0,
        command: event =>
          this.moveToIndex(bookmarkCategory, categories, 'start'),
      },
      {
        visible:
          this.permissionService.checkBookmarkRights(
            bookmarkCategory._id,
            'edit'
          ) && !bookmarkCategory.private,
        label: 'Ende',
        disabled:
          categories.indexOf(bookmarkCategory) === categories.length - 1,
        icon: 'fas fa-angles-right',
        command: event => this.moveToIndex(bookmarkCategory, categories, 'end'),
      },
    ];

    this.contextMenuBookmarkCategories = checkVisibleMenuItems(items);
  }

  /**
   * Moves a specified bookmark category within a list of categories to a new position based on the provided command.
   *
   * @param {BookmarkCategory} category - The bookmark category to be moved.
   * @param {BookmarkCategory[]} categories - The list of bookmark categories.
   * @param {'up'|'down'|'start'|'end'} command - The command determining the new position of the category.
   * @return {void} Updates the category list and their respective sorting order in the store.
   */
  moveToIndex(
    category: BookmarkCategory,
    categories: BookmarkCategory[],
    command: 'up' | 'down' | 'start' | 'end'
  ): void {
    const newArray = [...categories];
    const index = categories.indexOf(category);
    let newPosition = 0;
    switch (command) {
      case 'up':
        newPosition = index - 1;
        break;
      case 'down':
        newPosition = index + 1;
        break;
      case 'start':
        newPosition = 0;
        break;
      case 'end':
        newPosition = categories.length - 1;
        break;
    }

    const temp = newArray[newPosition];
    newArray[newPosition] = newArray[index];
    newArray[index] = temp;

    newArray.map((item: BookmarkCategory, idx) => {
      item.sorting = idx;
    });

    this.store.updateMultiple({ items: newArray });
  }

  /**
   * Navigates to the route for editing a bookmark category. If a specific bookmark category
   * is provided, it navigates to the editor for that category; otherwise, it navigates to
   * create a new category.
   *
   * @param {BookmarkCategory | undefined} bookmarkCategory - The bookmark category to edit, or undefined to create a new category.
   * @return {void} This method does not return a value.
   */
  editBookmarkCategory(bookmarkCategory: BookmarkCategory | undefined): void {
    this.router.navigate(
      [
        {
          outlets: {
            bearbeiten: [
              'bereich',
              bookmarkCategory ? bookmarkCategory._id : 'neu',
            ],
          },
        },
      ],
      { relativeTo: this.route }
    );
  }

  /**
   * Deletes a bookmark category after user confirmation.
   *
   * @param {MenuItemCommandEvent} event - The event associated with the delete action.
   * @param {BookmarkCategory} bookmarkCategory - The bookmark category to be deleted.
   * @return {void} This method does not return a value.
   */
  deleteBookmarkCategory(
    event: MenuItemCommandEvent,
    bookmarkCategory: BookmarkCategory
  ): void {
    this.store.singleSelection(bookmarkCategory._id);

    //this.confirm(event.originalEvent, bookmarkCategory, bookmarkCategory.title);
    setTimeout(() => {
      this.confirmationDialogService.confirmBookmarkCategoryDeletion(
        event.originalEvent,
        bookmarkCategory,
        () => this.remove(bookmarkCategory)
      );
    });
  }

  /**
   * Displays a toast message indicating an error when attempting to move an item.
   *
   * The toast message includes:
   * - Severity: error
   * - Summary: Verschieben nicht möglich
   * - Detail: Sie können ausschließlich eigene Ansichten in den privaten Ordner verschieben.
   * - Duration: 6000 milliseconds
   *
   * @return {void} This method does not return anything.
   */
  showDenyToast(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Verschieben nicht möglich',
      detail:
        'Sie können ausschließlich eigene Ansichten in den privaten Bereich verschieben.',
      life: 6000,
    });
  }

  /**
   * Opens the bookmark validation panel
   */
  openValidationPanel(): void {
    if (this.validationPanel) {
      this.validationPanel.open();
    }
  }
}
